import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, QrCode, BarChart3, Mail, ArrowRight, Sparkles, ShieldCheck, Rocket } from "lucide-react";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { trackPlausible } from "@/lib/plausible";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Tack. Fler återbesök för frisörer och salonger" },
      { name: "description", content: "Tack hjälper frisörer och salonger att få fler återbesök med QR-koder, rabatt via SMS och automatiska påminnelser. Prova gratis i 30 dagar." },
      { property: "og:title", content: "Tack. Fler återbesök för frisörer och salonger" },
      { property: "og:description", content: "Ge kunden en rabatt via SMS efter besöket och påminn innan den går ut. Ingen app eller kassaintegration." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Tack. Fler återbesök för frisörer och salonger" },
      { name: "twitter:description", content: "Ge kunden en rabatt via SMS efter besöket och påminn innan den går ut. Prova gratis i 30 dagar." },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/" }],
  }),
  component: LandingPage,
});

function LandingPage() {
  const { t } = useTranslation();
  const primaryCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const target = primaryCtaRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0.25 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const trackLandingAction = (placement: "hero" | "sticky" | "demo") => {
    trackPlausible("Landing CTA Clicked", { placement, audience: "salons" });
  };

  return (
    <div className="min-h-screen bg-background pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-0">
      <PaymentTestModeBanner />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
            <TackLogo size="md" showSlogan />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="dark" />
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/10 hover:text-primary">{t("common.login")}</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">{t("common.getStarted")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 pb-10 pt-10 sm:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-tack-warm px-4 py-1.5 text-sm font-medium text-primary sm:mb-6">
            <Star size={14} fill="currentColor" />
            {t("landing.eyebrow")}
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl md:text-6xl">
            {t("landing.heroA")} <span className="text-primary">{t("landing.heroB")}</span> {t("landing.heroC")}{" "}
            <span className="text-primary">{t("landing.heroD")}</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:mt-6 sm:text-lg">
            {t("landing.subtitle")}
          </p>
          <div ref={primaryCtaRef} className="mt-5 flex flex-col items-center justify-center gap-3 sm:mt-8 sm:flex-row">
            <Link to="/signup" onClick={() => trackLandingAction("hero")}>
              <Button size="lg" className="gap-2 px-8">
                {t("landing.primaryCta")}<ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/demo" onClick={() => trackLandingAction("demo")}>
              <Button variant="outline" size="lg" className="gap-2">
                <QrCode size={16} /> {t("common.tryDemo")}
              </Button>
            </Link>
          </div>

          {/* Hero video — vertical on mobile, wide on desktop */}
          <div className="mx-auto mt-8 flex max-w-3xl justify-center sm:mt-14">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(45,45,45,0.35)] ring-1 ring-border/40 bg-tack-warm w-full sm:max-w-none max-w-[380px]">
              {/* Mobile: vertical 9:16 */}
              <video
                src="/tack-hero-vertical.mp4?v=20260730a"
                poster="/tack-hero-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto block sm:hidden"
                aria-label="See how Tack turns a 5-second QR scan into a returning customer"
              />
              {/* Desktop / tablet: widescreen */}
              <video
                src="/tack-hero.mp4?v=20260730a"
                poster="/tack-hero-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto hidden sm:block"
                aria-label="See how Tack turns a 5-second QR scan into a returning customer"
              />
            </div>
          </div>
        </div>
      </section>




      {/* Why Tack */}
      <section className="py-20 px-4 border-t border-border/40">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center mb-12">
            {t("landing.whyTack.title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: QrCode, title: t("landing.whyTack.simpleLabel"), desc: t("landing.whyTack.simpleDesc") },
              { icon: Mail, title: t("landing.whyTack.secureLabel"), desc: t("landing.whyTack.secureDesc") },
              { icon: Rocket, title: t("landing.whyTack.startLabel"), desc: t("landing.whyTack.startDesc") },
            ].map((f, i) => (
              <Card key={i} className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <f.icon size={22} className="text-primary mb-3" />
                  <h3 className="font-display font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center mb-4">
            {t("landing.featuresTitle")}
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            {t("landing.featuresSubtitle")}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: QrCode,
                title: t("landing.features.qrTitle"),
                desc: t("landing.features.qrDesc"),
              },
              {
                icon: Star,
                title: t("landing.features.redeemTitle"),
                desc: t("landing.features.redeemDesc"),
              },
              {
                icon: BarChart3,
                title: t("landing.features.roiTitle"),
                desc: t("landing.features.roiDesc"),
              },
              {
                icon: Mail,
                title: t("landing.features.nudgeTitle"),
                desc: t("landing.features.nudgeDesc"),
              },
            ].map((f, i) => (
              <Card key={i} className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <f.icon size={20} className="text-primary mb-3" />
                  <h3 className="font-display font-semibold mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="py-20 px-4 bg-tack-warm">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-display font-bold mb-4">{t("landing.pricingTitle")}</h2>
          <p className="text-muted-foreground mb-8">
            {t("landing.pricingSubtitle")}
          </p>
          <Link to="/pricing">
            <Button size="lg" className="gap-2 px-8">
              {t("landing.seePricing", "Se priser & planer")} <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter variant="marketing" />

      <div
        className={`fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-lg backdrop-blur-md transition-transform duration-200 sm:hidden ${showStickyCta ? "translate-y-0" : "translate-y-full"}`}
        aria-hidden={!showStickyCta}
      >
        <Button asChild size="lg" className="w-full gap-2">
          <Link
            to="/signup"
            tabIndex={showStickyCta ? 0 : -1}
            onClick={() => trackLandingAction("sticky")}
          >
            {t("landing.primaryCta")}<ArrowRight size={16} />
          </Link>
        </Button>
      </div>

    </div>
  );
}
